import type { DocumentHandle } from '@platewell/core';
import {
  decodeJsonPayload,
  decodeSyncMessage,
  encodeSyncMessage,
  SyncMessageType,
  type SyncErrorPayload,
  type SyncInfoPayload,
} from '@platewell/schema';

export type ConnectionStatus =
  | 'offline'
  | 'connecting'
  | 'connected'
  | 'synced'
  | 'read_only'
  /** Payment failed and the grace period ran out. Distinct from 'error': sync is not broken. */
  | 'subscription_lapsed'
  | 'error';

export interface SyncConnectionOptions {
  url: string;
  handle: DocumentHandle;
  onStatus?: (status: ConnectionStatus) => void;
  onInfo?: (info: SyncInfoPayload) => void;
  /** The server has sent everything it holds; fires after every handshake, not just the first. */
  onSynced?: () => void;
  onServerError?: (error: SyncErrorPayload) => void;
  /** Injectable so tests can drive a fake socket. */
  webSocketImpl?: typeof WebSocket;
}

export interface SyncConnection {
  status(): ConnectionStatus;
  /** Last authoritative server time, used to clamp displayed timestamps. */
  serverTime(): number | null;
  connect(): void;
  disconnect(): void;
  destroy(): void;
}

const BASE_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

/**
 * WebSocket transport for one household document.
 *
 * It only ever moves opaque byte payloads: the handshake is a state-vector exchange and
 * everything after is an update frame, so this file needs no knowledge of Yjs at all.
 *
 * Reconnection uses exponential backoff with jitter capped at 30 seconds. There is no
 * presence or awareness traffic, so a flapping connection produces no thrash beyond the
 * reconnect itself.
 */
export function connectSync(options: SyncConnectionOptions): SyncConnection {
  const SocketImpl = options.webSocketImpl ?? globalThis.WebSocket;
  let socket: WebSocket | null = null;
  let status: ConnectionStatus = 'offline';
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let wantConnection = false;
  let lastServerTime: number | null = null;
  let readOnly = false;
  let lapsed = false;

  const setStatus = (next: ConnectionStatus): void => {
    if (status === next) return;
    status = next;
    options.onStatus?.(next);
  };

  const unsubscribe = options.handle.onUpdate((update, origin) => {
    // Never echo back what the server just sent, and never push what came off disk.
    if (origin === 'remote' || origin === 'load') return;
    if (readOnly) return;
    if (socket?.readyState !== SocketImpl.OPEN) return;
    socket.send(encodeSyncMessage(SyncMessageType.Update, update));
  });

  function scheduleRetry(): void {
    if (destroyed || !wantConnection || retryTimer != null) return;
    const backoff = Math.min(BASE_RETRY_MS * 2 ** attempt, MAX_RETRY_MS);
    const jitter = Math.random() * backoff * 0.3;
    attempt += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      open();
    }, backoff + jitter);
  }

  function open(): void {
    if (destroyed || socket != null) return;
    setStatus('connecting');

    const ws = new SocketImpl(options.url);
    ws.binaryType = 'arraybuffer';
    socket = ws;

    ws.onopen = () => {
      attempt = 0;
      readOnly = false;
      lapsed = false;
      setStatus('connected');
      // Tell the server what we already have; it replies with only the difference.
      ws.send(encodeSyncMessage(SyncMessageType.StateVector, options.handle.encodeStateVector()));
    };

    ws.onmessage = (event: MessageEvent) => {
      const decoded = decodeSyncMessage(event.data as ArrayBuffer);
      if (decoded == null) return;

      switch (decoded.type) {
        case SyncMessageType.Update:
          options.handle.applyUpdate(decoded.payload, 'remote');
          return;

        case SyncMessageType.StateVector:
          // The server's view; send back whatever it is missing from us.
          ws.send(
            encodeSyncMessage(
              SyncMessageType.Update,
              options.handle.encodeStateAsUpdate(decoded.payload),
            ),
          );
          return;

        case SyncMessageType.Synced:
          setStatus(readOnly ? 'read_only' : 'synced');
          options.onSynced?.();
          return;

        case SyncMessageType.Info: {
          const info = decodeJsonPayload<SyncInfoPayload>(decoded.payload);
          lastServerTime = info.serverTime;
          readOnly = info.role === 'viewer';
          options.onInfo?.(info);
          if (readOnly) setStatus('read_only');
          return;
        }

        case SyncMessageType.Error: {
          const error = decodeJsonPayload<SyncErrorPayload>(decoded.payload);
          options.onServerError?.(error);
          if (error.code === 'read_only') {
            readOnly = true;
            setStatus('read_only');
          } else if (error.code === 'quota_exceeded' || error.code === 'recipe_ceiling') {
            // Advisory, and sync is perfectly healthy. Flipping to 'error' here would put
            // "Sync problem — retrying automatically" in the topbar over a working
            // connection; the payload reaches the app through onServerError instead.
          } else if (error.code === 'subscription_lapsed') {
            lapsed = true;
            setStatus('subscription_lapsed');
          } else {
            setStatus('error');
          }
          // Authorization failures are terminal: retrying cannot fix being removed from a
          // household, and hammering the endpoint would only look like an attack.
          if (
            error.code === 'unauthorized' ||
            error.code === 'forbidden' ||
            error.code === 'household_deleted'
          ) {
            wantConnection = false;
          }
          // `subscription_lapsed` is deliberately *not* terminal. The Worker refuses the
          // upgrade with a 402 while a household is locked, so the backoff retry is what
          // brings it back the moment a recovered payment lands — no user action, no
          // reload, which is the whole point of the RENEWAL webhook path.
          return;
        }

        default:
          return;
      }
    };

    ws.onclose = () => {
      socket = null;
      // A lapsed household keeps that status across the close, so the shell goes on
      // rendering the lock screen instead of flickering to the offline indicator.
      const closed: ConnectionStatus = lapsed ? 'subscription_lapsed' : 'offline';
      setStatus(closed);
      if (!destroyed && wantConnection) scheduleRetry();
    };

    ws.onerror = () => {
      setStatus('error');
    };
  }

  return {
    status: () => status,
    serverTime: () => lastServerTime,
    connect: () => {
      wantConnection = true;
      attempt = 0;
      open();
    },
    disconnect: () => {
      wantConnection = false;
      if (retryTimer != null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      socket?.close();
      socket = null;
      setStatus('offline');
    },
    destroy: () => {
      destroyed = true;
      wantConnection = false;
      if (retryTimer != null) clearTimeout(retryTimer);
      unsubscribe();
      socket?.close();
      socket = null;
    },
  };
}
